/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import Services from '../services';
import * as ActionCreators from '../store/state/actions/action-creators';
import { dispatch, getStore } from '../store/state/state-manager';
import * as ContextUtils from '../utils/context-utils';
import Utils from '../utils/utils';
import EnvironmentInfo from '../environment-info';
import RemoteCase, { REMOTE_CASE_VIEW } from '../case/remote-case';
import type { RemoteCaseHistoryActionObject } from '../globals';
import { OPENCASE } from '../store/state/actions';
import CaseUtils from '../case/utils';

import * as BrowserHistory from './browser-router';
import RouteParser from './route-parser';
import * as RoutingManager from './routing-manager';
import { getSemanticURLWithRelatedQueryParams, resolveRouteInfoParams, isNextGenLandingPageRouting } from './helper';
import { capitalizeFirstLetter, GlobalTOKEN, hasNumber, isDynamicPattern, trimURL, convertToKebabCase } from './utils';
import type {
  BrowserBookmarkOptions,
  BuildRestAPIPayload,
  RestAPIPayload,
  RoutingInfo,
  ShowHistoryActionOptions,
  RouteObjectPayload
} from './types';

/**
 * @file Implements Semantic URL Logic: consists of methods for handling application routing
 * @author WebWiz Team <WebWizDevelopmentTeam@pega.com>
 */

const VIEW = 'VIEW';
const PAGE = 'PAGE';
const ADD_TO_HISTORY = 'add';
const REPLACE_IN_HISTORY = 'replace';
const GLOBAL_QUERYPARAMS = ['portal', 'noPortal', 'preview', 'debug', 'devtool', 'activeTab'];

let domain = '';
let defaultViewUrlpath = 'Home';
let applicationLabel = '';
let bRouting = true;

/**
 * this method is a toggle feature to enable or disable application routing.
 * @param bEnable boolean to toggle app routing
 */
export const enableAppRouting = (bEnable: boolean) => {
  bRouting = bEnable;
  return bRouting;
};

/**
 * this method is a toggle feature to enable or disable application routing
 * @function isAppRoutingEnabled
 * @returns isAppRoutingEnabled Flag
 */
export const isAppRoutingEnabled = () => bRouting;

/**
 * Get semantic url for given routeKey (ex: showView)
 * @function getSemanticURL
 * @param routeKey - routeKey
 * @param payload - payload
 * @param queryParameters - queryParameters
 * @returns semantic url or empty string
 */
export const getSemanticURL = (routeKey: string, payload: any, queryParameters?: any) => {
  return RouteParser.getSemanticURL(routeKey, payload, queryParameters);
};

/**
 * Get restApi for given routeKey (ex: showView)
 * @function getRestEndPoint
 * @param routeKey - routeKey
 * @param payload - payload
 * @returns restApi and reqType
 */
export const getRestEndPoint = (routeKey: string, payload?: BuildRestAPIPayload) => {
  return RouteParser.getRestEndPoint(routeKey, payload);
};

/**
 * build and return complete rest url including host.
 * @function getRestEndPoint
 * @param routeKey - routeKey
 * @param payload - payload
 * @returns object containing restApi and reqType properties if the passed routeKey is present in routing info else null.
 */
export const resolveRestApi = (routeKey: string, payload?: BuildRestAPIPayload): RestAPIPayload => {
  let serverUrl = Services.getServerURL();

  const restObj = getRestEndPoint(routeKey, payload);
  if (!restObj) {
    return {};
  }

  if (!restObj.restApi?.startsWith('/')) {
    restObj.restApi = `/${restObj.restApi}`;
  }

  if (serverUrl?.endsWith('/')) {
    serverUrl = serverUrl.substring(0, serverUrl.length - 1);
  }
  restObj.restApi = serverUrl + restObj.restApi;
  return restObj;
};

/**
 * this method returns true if View is a Page, otherwise it returns false
 * @function isViewAPage
 * @param uiResources - uiResources information
 * @returns isViewAPage Flag
 */
const isViewAPage = (uiResources: any) => {
  if (uiResources && uiResources.root) {
    const { root } = uiResources;
    const { resources } = uiResources;
    const { views } = resources;
    const respRoot = root;

    const rootConfig = respRoot.config;
    const rootConfigType = rootConfig.type;
    const rootConfigName = rootConfig.name;

    if (rootConfigType.toUpperCase() === VIEW) {
      const view = views[rootConfigName];
      let viewObj = view;
      if (Array.isArray(view) && view.length > 0) {
        // take first element if view is an array
        [viewObj] = view;
      }
      const viewObjConfig = viewObj.config;
      if (viewObjConfig.type) {
        // type is optional and will only be there for page
        // and web_shell
        const viewType = viewObjConfig.type;
        return !!(viewObjConfig.urlPath && viewType.toUpperCase() === PAGE);
      }
    }
    return false;
  }
  return false;
};

/**
 * this method is used to read semantic URL/URL pattern from pxURLPath and return semantic URL Information
 * @function getURLPathInfo
 * @param uiResources - uiResources information
 * @param responseData - response.data
 * @param url - pxURLPath
 * @returns an object with URLpath and addToHistory Flag
 */
const getURLPathInfo = (uiResources: any, responseData: any, url?: string) => {
  // pass third parameter response.data in _getURLpathInfo
  const { root, resources } = uiResources;
  const { views } = resources;
  const { config: rootConfig } = root;
  const historyAction = ADD_TO_HISTORY; // historyAction is set to addHistory
  let pxURLPath = '';
  if (url) {
    pxURLPath = url;
  } else {
    const view = views[rootConfig.name];
    let viewObj = view;
    if (Array.isArray(view) && view.length > 0) {
      // take first element if view is an array
      [viewObj] = view;
    }
    const viewObjConfig = viewObj.config;
    pxURLPath = viewObjConfig.urlPath;
  }

  const dynamicTokens = pxURLPath.match(GlobalTOKEN);
  if (dynamicTokens) {
    const context = Utils.getIn(responseData, rootConfig.context);
    pxURLPath = pxURLPath.replace(dynamicTokens[0], context.pyID);
  }

  if (pxURLPath.startsWith('/')) {
    pxURLPath = pxURLPath.substring(1, pxURLPath.length);
  }

  return { pxURLPath, historyAction };
};

/**
 * this method appends global params like portal and noPortal if is present in the url. Don't append non global query params in the current url.
 * Eg: Query params of data objects when navigating from data object page to other pages like Home or MyWork
 * @function appendGlobalParams
 * @param queryParams - application label
 * @param searchParamsObj - application name
 */
const appendGlobalParams = (queryParams: any, searchParamsObj: any) => {
  for (const [key, value] of searchParamsObj.entries()) {
    if (!queryParams.has(key) && GLOBAL_QUERYPARAMS.includes(key)) {
      queryParams.append(key, value);
    }
  }
  return queryParams;
};

const adjustURLForExecutionContext = (
  semanticURL: string,
  theDomain: string,
  appName: string,
  lastToken: string,
  searchParamsObj: URLSearchParams
) => {
  const defaultStaticRoute = RouteParser.getStaticRoutePage();
  const defaultPath = defaultViewUrlpath?.toLowerCase();
  const staticRoute = defaultStaticRoute?.toLowerCase();
  const last = lastToken?.toLowerCase();

  const matchesDefaultView = staticRoute === defaultPath || last === defaultPath;

  if (matchesDefaultView && !searchParamsObj.has('noPortal')) {
    return `${theDomain}/${appName}`;
  }

  return semanticURL;
};

/**
 * this method builds semantic URL
 * @function buildSemanticURL
 * @param theDomain - domain name
 * @param appName - application name
 * @param urlPathObj - url path object
 */
const buildSemanticURL = (theDomain: string, appName: string, urlPathObj: any) => {
  let semanticURL;
  let queryParams;

  if (trimURL(appName)) {
    semanticURL = `${theDomain}/${appName}`;
  } else {
    semanticURL = theDomain;
  }

  const urlPath = trimURL(urlPathObj?.pxURLPath);
  if (urlPath) {
    semanticURL = `${semanticURL}/${urlPath}`;
  }

  try {
    queryParams = new URL(semanticURL).searchParams;
    semanticURL = semanticURL.includes('?') ? semanticURL.split('?')[0] : semanticURL;
  } catch (e) {
    console.log('Invalid url', e);
    return '';
  }

  const tokens = semanticURL.split('/');
  const lastToken = tokens[tokens.length - 1];
  const searchParamsObj = new URLSearchParams(BrowserHistory.getQueryParams());

  if (isNextGenLandingPageRouting()) {
    semanticURL = adjustURLForExecutionContext(semanticURL, theDomain, appName, lastToken, searchParamsObj);
    RouteParser.setStaticRoutePage('');
  } else if (lastToken.toLowerCase() === defaultViewUrlpath.toLowerCase() && !searchParamsObj.has('noPortal')) {
    semanticURL = `${theDomain}/${appName}`;
  }

  if (!RouteParser.isDefaultPortal()) {
    const portalName = RouteParser.getPortalName();
    if (searchParamsObj.has('portal')) {
      searchParamsObj.set('portal', portalName);
    } else {
      searchParamsObj.append('portal', portalName);
    }
  }

  queryParams = appendGlobalParams(queryParams, searchParamsObj);

  if (queryParams.toString()) {
    semanticURL += `?${queryParams.toString()}`;
  }

  return semanticURL;
};

/**
 * this method builds semantic URL
 * @function buildTitle
 * @param applicationLabel - application label
 * @param appName - application name
 * @param semanticURL - semantic url
 */
// eslint-disable-next-line @typescript-eslint/no-shadow
const buildTitle = (applicationLabel: string, appName: string, semanticURL: string) => {
  applicationLabel = applicationLabel && applicationLabel !== '' ? applicationLabel : RouteParser.getApplicationName();
  applicationLabel = capitalizeFirstLetter(applicationLabel);

  if (semanticURL === `${domain}/${appName}/`) {
    return { title: applicationLabel };
  }

  const url = new URL(semanticURL);
  const route = url.pathname.replace(`${appName}/`, '');
  const tokens = route.split('/');

  if (hasNumber(route)) {
    return { title: `${applicationLabel} - ${tokens[tokens.length - 1]}` };
  }
  return { title: `${applicationLabel} - ${tokens[0]}` };
};

/**
 * this method is used to call other methods and finally update/replace semantic url in browser
 * @function resolveAndUpdateSemanticURL
 * @param translatedData - { uiResources, data, options }
 * @param optionalInformation - {urlPathObjParam, infoParam, optionsParam}
 * @param isURLBookmarked - is URL Bookmarked?
 */
const resolveAndUpdateSemanticURL = (
  translatedData: any,
  {
    urlPathObjParam,
    infoParam,
    optionsParam,
    queryParam
  }: {
    urlPathObjParam: { historyAction: string; pxURLPath: string } | null;
    infoParam: any;
    optionsParam: any;
    queryParam?: string | null;
  },
  isURLBookmarked = false
) => {
  let urlPathObj;
  let info;
  let options;

  // If override has turned off use of dynamic Semantic URLs, just return

  if (window.PCore.getBehaviorOverride('dynamicSemanticUrl') === false) {
    return;
  }
  if (!isURLBookmarked) {
    const { uiResources, data } = translatedData;
    ({ options } = translatedData);
    if (urlPathObjParam && urlPathObjParam.pxURLPath && isDynamicPattern(urlPathObjParam.pxURLPath)) {
      urlPathObj = getURLPathInfo(uiResources, data, urlPathObjParam.pxURLPath);
    } else {
      urlPathObj = urlPathObjParam || getURLPathInfo(uiResources, data);
    }
    info = RouteParser.getEndPointInfo(urlPathObj.pxURLPath);
    urlPathObj.pxURLPath = info.urlPath;
  } else {
    urlPathObj = urlPathObjParam;
    info = infoParam;
    options = optionsParam;
  }

  let semanticURL = buildSemanticURL(domain, RouteParser.getApplicationPath(), urlPathObj);
  /* Building title only for browser though this is not a required parameter and title is completely ignore by browsers.
        Title build here is not in sync with document title as we don't have enough information to build proper title here.
    */
  // Ref: https://github.com/whatwg/html/issues/2174
  const { title } = buildTitle(applicationLabel, RouteParser.getApplicationPath(), semanticURL);
  const state = BrowserHistory.getStateForSemanticURL(info, { ...options, title });

  if (urlPathObj?.historyAction === ADD_TO_HISTORY) {
    BrowserHistory.addToHistory(state, title, semanticURL);
  } else if (urlPathObj?.historyAction === REPLACE_IN_HISTORY) {
    if (queryParam) {
      semanticURL += `?${queryParam}`;
    }
    BrowserHistory.replaceHistory(state, title, semanticURL);
  }
};

/**
 * this method is wrapper function for resolveAndUpdateSemanticURL
 * @function routeProcessorInternal
 * @param translatedData - { uiResources, data, options }
 */
const routeProcessorInternal = (translatedData: any) => {
  let urlPathObjParam: { historyAction: string; pxURLPath: string } | null = null;
  if (translatedData.directUrlUpdation) {
    urlPathObjParam = { historyAction: ADD_TO_HISTORY, pxURLPath: '' };
    urlPathObjParam.pxURLPath = translatedData.semanticURL;
  }
  resolveAndUpdateSemanticURL(translatedData, { urlPathObjParam, infoParam: null, optionsParam: null }, false);
};

/**
 * this method is wrapper function for _routeProcessor
 * @function routeProcessorInternal
 * @param translatedData - { uiResources, data, options }
 */
export const routeProcessor = (translatedData: any) => {
  routeProcessorInternal(translatedData);
};

/**
 * this method is wrapper function for _buildSemanticURL
 * @param urlPath - object containing pxURLPath
 * @returns - url containing caseId in case of case link along with application domain and app name
 */
export const resolveSemanticURL = (urlPath: string): string => {
  const urlPathObj = { pxURLPath: urlPath };
  return buildSemanticURL(domain, RouteParser.getApplicationPath(), urlPathObj);
};

/**
 * this method is a public API exposed to other modules
 * @function parseResponse
 * @param translatedData - { uiResources, data, options }
 */
export const parseResponse = (translatedData: any) => {
  const { uiResources } = translatedData;
  if (isAppRoutingEnabled() && (translatedData.directUrlUpdation || isViewAPage(uiResources))) {
    routeProcessorInternal(translatedData);
  }
};

/**
 * this method builds action object from history state object
 * @function buildActionObject
 * @param obj - history state object
 * @returns action Object
 */
const buildActionObject = (obj: { [key: string]: any }) => {
  const returnObj: { [key: string]: any } = {
    routeEndpoint: obj.routeEndpoint,
    requestType: obj.requestType,
    payload: obj.payload,
    routeKey: obj.routeKey,
    options: null
  };
  if (obj.options) {
    returnObj.options = obj.options;
  }

  return returnObj;
};

/**
 * @function
 * @returns void
 * @private
 */
const dispatchRemoteCaseBrowserBookmark = (
  data: ShowHistoryActionOptions,
  options: BrowserBookmarkOptions,
  actionContext?: string
) => {
  const { caseClassName, workID } = data.payload;
  const { containerName, context } = options;
  const { RemoteClassGroup, RemoteSystemID, name } = RemoteCase.getRemoteCase(caseClassName);
  const actionConfig: RemoteCaseHistoryActionObject = {
    ...data,
    headers: {
      RemoteSystemID
    },
    className: caseClassName,
    isTraditionalRemoteCase: true,
    action: OPENCASE,
    viewName: REMOTE_CASE_VIEW,
    gadgetId: CaseUtils.getUniqueGadgetId(),
    context: actionContext ?? context,
    skipSemanticUrl: true,
    insKey: `${RemoteClassGroup?.toUpperCase()} ${workID}`,
    containerName,
    name
  };

  const actionName = 'showPage';
  dispatch(ActionCreators[actionName](actionConfig));
};

/**
 * this method listens to browser back/forward event
 * @function publishForBrowserHistory
 * @param event - popstate event
 */
export const publishForBrowserHistory = (event: any) => {
  const actionName = 'showHistory';
  const stateObj = BrowserHistory.getStateForBackForwardNav(event);

  if (stateObj) {
    if (RemoteCase.isTraditionalRemoteCase(stateObj.payload?.caseClassName)) {
      dispatchRemoteCaseBrowserBookmark({ ...stateObj }, stateObj.options);
    } else {
      dispatch(ActionCreators[actionName](buildActionObject(stateObj)));
    }
  }
};

/**
 * this method checks if browser url is a semantic url or not.
 * @function isSemanticURL
 * @returns isSemanticURL Flag
 */
export const isSemanticURL = () => {
  const appName = RouteParser.getApplicationPath();
  const urlPath = BrowserHistory.getPathNameOfUrl();
  return appName !== urlPath;
};

/**
 * Get the restApi for the given view
 * @param viewID - View Name passed in the queryparam
 * @param viewClass - View Name passed in the queryparam
 * @returns restApi - restApi for the view
 */
export const getViewRestEndPoint = (viewID: string, viewClass: string): RestAPIPayload => {
  let restApiObj: RestAPIPayload = { restApi: '' };
  try {
    if (isSemanticURL() && !viewClass) {
      const urlPath = BrowserHistory.getPathNameOfUrl(); // "prweb/app/AppName/caseType/{caseId}"
      const semanticURL = RouteParser.processUrl(urlPath, true); // "caseType/{caseId}"
      const matchedRoute = RouteParser._getMatchedRouteInRoutingTable(semanticURL) || '';
      const routingInfo = RouteParser.getRoutingInfo();
      const tokens = RouteParser.processUrl(urlPath, false).split('/');
      const workID = tokens[tokens.length - 1];
      let { caseID } = routingInfo && routingInfo.routes[matchedRoute].payload;
      caseID = caseID.replace('{workID}', workID).replace('{id}', workID);
      restApiObj = RouteParser.getRestEndPoint('loadView', { caseID, viewID }) || {};
    } else {
      restApiObj = RouteParser.getRestEndPoint('showView', { page: viewID, pageClass: viewClass }) || {};
    }
  } catch (e) {
    // console.error("Semantic URL is not in case context");
  }
  return restApiObj;
};

/**
 * this method checks if browser url is a valid semantic url or not.
 * @function isValidSemanticURL
 * @returns isSemanticURL Flag
 */
export const isValidSemanticURL = () => {
  if (isSemanticURL()) {
    const semanticURL = RouteParser.processUrl(BrowserHistory.getPathNameOfUrl(), true);
    const matchedRoute = RouteParser._getMatchedRouteInRoutingTable(semanticURL);

    return !!matchedRoute;
  }
  return false;
};

/**
 * API to return the defaultRoute name
 * @param defaultViewLabel - Default view label configured in portal channel
 */
const buildDefaultRoute = (defaultViewLabel: string, className: string) => {
  const routingInfo = RouteParser.getRoutingInfo();

  if (isNextGenLandingPageRouting()) {
    const routesArray = Object.values(routingInfo?.routes);

    const matchedRoute = (routesArray as RouteObjectPayload[]).find(
      (route: RouteObjectPayload) =>
        route?.payload?.pageClass === className && route?.payload?.page === defaultViewLabel
    );
    if (matchedRoute) {
      return defaultViewLabel;
    }
  }
  // Server Logic: Initially routeName is built by removing spaces and joining. Later removing spaces and joining by -. Checking for both possibilities to find the routeName
  const spaceRemovedRouteName = defaultViewLabel.split(' ').join('');
  const hypenJoinedRouteName = defaultViewLabel.split(' ').join('-').toLowerCase();
  const kebabCaseRouteName = convertToKebabCase(defaultViewLabel);

  if (routingInfo?.routes[spaceRemovedRouteName]) {
    return spaceRemovedRouteName;
  }

  if (routingInfo?.routes[hypenJoinedRouteName]) {
    return hypenJoinedRouteName;
  }

  if (routingInfo?.routes[kebabCaseRouteName]) {
    return kebabCaseRouteName;
  }
  return defaultViewLabel;
};

/**
 * API to set defaultRoute name
 * @param defaultRoute - Default route of portal channel
 */
export const setDefaultRoute = (defaultRoute: string) => {
  defaultViewUrlpath = defaultRoute;
};

/**
 * API to get defaultRoute name
 * @returns - defaultViewUrlpath - Default route of portal channel
 */
export const getDefaultRoute = () => {
  return defaultViewUrlpath;
};

/**
 * this method publish for browser bookmark or refresh in viewcontainer (primary)
 * @function publishForBrowserBookmark
 * @param options - options
 */
export const publishForBrowserBookmark = (options: BrowserBookmarkOptions) => {
  const { acName, context, defaultViewLabel, ruleName, className } = options;
  const defaultRoute = buildDefaultRoute(ruleName || defaultViewLabel, className);
  setDefaultRoute(defaultRoute);
  RouteParser.setDefaultRoute(defaultRoute);
  RouteParser.setDefaultClassName(className);
  const newContext = ContextUtils.buildContextName(acName, context);
  const newTarget = RoutingManager.getResolvedTargetName(acName, context);
  const defaultHomeCallRequired = EnvironmentInfo.getIsExtraCallRequiredForDefaultHome() === 'true';

  const urlPathObjParam = { historyAction: REPLACE_IN_HISTORY, pxURLPath: '' };
  let infoParam;
  let queryParam = null;
  const optionsParam = {
    acName: newTarget,
    context: newContext,
    actionConfig: { context: newContext }
  };

  if (defaultHomeCallRequired || (!defaultHomeCallRequired && isSemanticURL())) {
    const url = getSemanticURLWithRelatedQueryParams(BrowserHistory.getPathNameWithQueryParams());
    if (EnvironmentInfo.shouldUseURLParams()) {
      queryParam = url.includes('?') ? url.split('?')[1] : null;
    }
    infoParam = resolveRouteInfoParams(url, optionsParam);
    urlPathObjParam.pxURLPath = infoParam.urlPath;
    if (typeof infoParam.payload?.pageClass !== 'undefined') {
      infoParam.payload.pageClass = getStore()?.getState()?.data?.app?.pyPortal?.classID;
    }
    const actionName = 'showHistory';
    const payload: ShowHistoryActionOptions = {
      routeEndpoint: infoParam.routeEndpoint,
      requestType: infoParam.requestType,
      payload: infoParam.payload,
      options: optionsParam,
      routeKey: infoParam.routeKey
    };

    // check and dispatch showPage for traditional remote case
    if (RemoteCase.isTraditionalRemoteCase(infoParam?.payload?.caseClassName)) {
      dispatchRemoteCaseBrowserBookmark(payload, options, optionsParam.context);
    } else {
      dispatch(ActionCreators[actionName](buildActionObject(payload)));
    }
  } else {
    infoParam = RouteParser.getEndPointInfo(defaultViewUrlpath);
    urlPathObjParam.pxURLPath = '';
  }
  resolveAndUpdateSemanticURL(null, { urlPathObjParam, infoParam, optionsParam, queryParam }, true);
};

/**
 * This method initializes instance variables of Class
 * @function init
 * @param routingInfo - Application Routing Table Information
 * @param disableBrowserNavigation local mechanism to disable app navigation through browser history
 */
export const init = (routingInfo: RoutingInfo, disableBrowserNavigation = false) => {
  domain = routingInfo.domain;
  if (disableBrowserNavigation === false && isAppRoutingEnabled()) {
    BrowserHistory.addEventListenerForHistory(publishForBrowserHistory);
    BrowserHistory.addEventListenerForRefresh();
  }
  RouteParser.init(routingInfo, defaultViewUrlpath);
};

/**
 * This method merges the existing routingInfo with the passed routingInfo
 * @function addRoutes
 * @param routingInfo - Application Routing Table Information
 */
export const addRoutes = (routingInfo: any) => {
  RouteParser.addRoutes(routingInfo);
};

/**
 * this method reads value from Application.pyLabel
 * @function initializeApplicationLabel
 * @param applicationData - response.data
 */
export const initializeApplicationLabel = (applicationData: any) => {
  if (applicationData) {
    applicationLabel = applicationData.pyLabel;
  }
};

/**
 * this method update document title
 * @function updateDocumentTitle
 * @param title - document title
 */
export const updateDocumentTitle = (title: string) => {
  BrowserHistory.updateDocumentTitle(title);
};
